/**
 * Recruitment ATS — domain constants.
 * Exact vocabulary and fixed enums per the design brief (rules R5, R7) and the API contract.
 */

import type { Stage, JobStatus, EmploymentType } from '../../../services/recruitmentApi';

// ─── Stages (fixed order; rule R5) ───────────────────────────────────────────

/** Pipeline flow order, left → right. */
export const STAGES: Stage[] = [
	'new',
	'screened',
	'shortlisted',
	'interview',
	'offer',
	'hired',
	'rejected',
	'on_hold',
];

/** Columns shown on the Kanban board (same set, board layout). */
export const PIPELINE_STAGES: Stage[] = STAGES;

type Tone = {
	/** badge/pill classes */
	badge: string;
	/** kanban column accent (top border) */
	accent: string;
};

export const STAGE_META: Record<Stage, { label: string; tone: Tone }> = {
	new: { label: 'New', tone: { badge: 'bg-gray-100 text-gray-700 border-gray-200', accent: 'border-t-gray-300' } },
	screened: { label: 'Screened', tone: { badge: 'bg-teal-50 text-teal-700 border-teal-200', accent: 'border-t-teal-400' } },
	shortlisted: { label: 'Shortlisted', tone: { badge: 'bg-purple-50 text-purple-700 border-purple-200', accent: 'border-t-purple-400' } },
	interview: { label: 'Interview', tone: { badge: 'bg-amber-50 text-amber-700 border-amber-200', accent: 'border-t-amber-400' } },
	offer: { label: 'Offer', tone: { badge: 'bg-emerald-50 text-emerald-700 border-emerald-300', accent: 'border-t-emerald-400' } },
	hired: { label: 'Hired', tone: { badge: 'bg-green-600 text-white border-green-600', accent: 'border-t-green-500' } },
	rejected: { label: 'Rejected', tone: { badge: 'bg-red-50 text-red-700 border-red-200', accent: 'border-t-red-400' } },
	on_hold: { label: 'On-hold', tone: { badge: 'bg-gray-100 text-gray-500 border-gray-300', accent: 'border-t-gray-300' } },
};

export const stageLabel = (stage: string): string =>
	STAGE_META[stage as Stage]?.label ?? stage;

// ─── Job status ──────────────────────────────────────────────────────────────

export const JOB_STATUS_META: Record<JobStatus, { label: string; badge: string }> = {
	open: { label: 'Open', badge: 'bg-green-50 text-green-700 border-green-200' },
	on_hold: { label: 'On-hold', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
	closed: { label: 'Closed', badge: 'bg-gray-100 text-gray-600 border-gray-200' },
	filled: { label: 'Filled', badge: 'bg-blue-50 text-blue-700 border-blue-200' },
};

export const JOB_STATUS_OPTIONS: { value: JobStatus; label: string }[] = (
	Object.keys(JOB_STATUS_META) as JobStatus[]
).map(value => ({ value, label: JOB_STATUS_META[value].label }));

// ─── Employment type ─────────────────────────────────────────────────────────

export const EMPLOYMENT_TYPE_META: Record<EmploymentType, string> = {
	full_time: 'Full-time',
	part_time: 'Part-time',
	contract: 'Contract',
	intern: 'Intern',
};

export const EMPLOYMENT_TYPE_OPTIONS: { value: EmploymentType; label: string }[] = (
	Object.keys(EMPLOYMENT_TYPE_META) as EmploymentType[]
).map(value => ({ value, label: EMPLOYMENT_TYPE_META[value] }));

// ─── Source (mandatory on every application; rule R4) ─────────────────────────

export const SOURCE_OPTIONS: { value: string; label: string }[] = [
	{ value: 'linkedin', label: 'LinkedIn' },
	{ value: 'naukri', label: 'Naukri' },
	{ value: 'indeed', label: 'Indeed' },
	{ value: 'referral', label: 'Referral' },
	{ value: 'careers_page', label: 'Careers page' },
	{ value: 'walk_in', label: 'Walk-in' },
	{ value: 'other', label: 'Other' },
];

export const sourceLabel = (source: string): string =>
	SOURCE_OPTIONS.find(o => o.value === source)?.label ?? source;

// ─── Stage-move reason presets (rule R3: reason required in the UI) ───────────

export const REASON_PRESETS: string[] = [
	'Not enough experience',
	'Location mismatch',
	'Strong interview',
	'Better fit elsewhere',
	'Notice period too long',
	'Meets basic requirements',
	'Other…',
];

/** The sentinel that means "type your own reason". */
export const REASON_OTHER = 'Other…';
